import { useQuery } from '@tanstack/react-query';
import config from '@/config';
import type { RebateCalculationAudit } from './types/auditTypes';

interface UseRebateCalculationAuditOptions {
  agreementId: string | undefined;
  date?: Date;
  calculationType?: 'estimate' | 'prediction';
}

/**
 * Fetch calculation audit data for a rebate agreement
 * Shows exactly how rebate amounts were calculated
 */
export function useRebateCalculationAudit({
  agreementId,
  date,
  calculationType = 'estimate',
}: UseRebateCalculationAuditOptions) {
  return useQuery({
    queryKey: [
      'rebate-calculation-audit',
      agreementId,
      date?.toISOString().split('T')[0],
      calculationType,
    ],
    queryFn: async (): Promise<RebateCalculationAudit> => {
      if (!agreementId) {
        throw new Error('Agreement ID is required');
      }

      const params = new URLSearchParams({
        agreementId,
        calculationType,
      });

      if (date) {
        params.append('date', date.toISOString().split('T')[0]);
      }

      const url = `${config.apiBaseUrl}/rebates/audit/calculation?${params}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: 'Failed to fetch audit',
        }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return response.json();
    },
    enabled: !!agreementId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
